import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { StatuteRenderer } from './StatuteRenderer';
import { findStatuteSection, useStatuteReaderSections } from '../hooks/useStatuteReaderSections';
import { formatAuthorityChipLabel } from '../lib/formatAuthorityLabel';
import type { Source } from '../lib/ai';

interface FloatingStatuteCitationProps {
  source: Source;
  onClose: () => void;
}

export function FloatingStatuteCitation({ source, onClose }: FloatingStatuteCitationProps) {
  const { statute, sections, loading, error } = useStatuteReaderSections(source.statuteId);
  const section = useMemo(
    () => findStatuteSection(sections, source.sectionId),
    [sections, source.sectionId],
  );
  const { primary, secondary } = formatAuthorityChipLabel(source, statute?.title);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const libraryTarget = source.sectionId
    ? `/library/${source.statuteId}#${source.sectionId}`
    : `/library/${source.statuteId}`;

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close statute citation"
        className="fixed inset-0 z-[75] bg-black/20"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={primary}
        className="fixed z-[80] left-4 bottom-20 w-[min(400px,calc(100vw-2rem))] max-h-[min(480px,70vh)] flex flex-col rounded-xl border border-white/10 bg-[#1a1a1a] shadow-2xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-white/10 bg-[#222]">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white leading-snug">{primary}</p>
            {secondary ? (
              <p className="mt-0.5 text-xs text-slate-400 leading-snug line-clamp-2">{secondary}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-slate-400 hover:text-white hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 text-sm text-slate-200 leading-relaxed">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading statute…</span>
            </div>
          ) : error ? (
            <p className="text-amber-300/90">{error}</p>
          ) : section ? (
            <StatuteRenderer data={section} />
          ) : source.snippet ? (
            <p className="whitespace-pre-wrap">{source.snippet}</p>
          ) : (
            <p className="text-slate-400">Section text is not available.</p>
          )}
        </div>

        <div className="shrink-0 px-4 py-2 border-t border-white/10 bg-[#222]">
          <Link
            to={libraryTarget}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 no-underline"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open in Library
          </Link>
        </div>
      </div>
    </>,
    document.body,
  );
}
